import { Component, inject, signal } from "@angular/core"
import { HttpClient } from "@angular/common/http"
import { takeUntilDestroyed } from "@angular/core/rxjs-interop"
import { NgSwitch, NgSwitchCase, NgSwitchDefault } from "@angular/common"
import {
    MAT_DIALOG_DATA,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
} from "@angular/material/dialog"
import { map, of, switchMap, throwError } from "rxjs"
import { MatIcon } from "@angular/material/icon"
import { ContextService, HistoryService } from "src/app/services"
import { environment } from "src/environments/environment"
import { SurveyHistoryComponent } from "./survey-history.component"
import { BenefitsHistoryComponent } from "./benefits-history.component"
import { CategoriesHistoryComponent } from "./categories-history.component"
import { LocationsHistoryComponent } from "./locations-history.component"
import { AwardsHistoryComponent } from "./awards-history.component"
import { NewsHistoryComponent } from "./news-history.component"
import { PerksHistoryComponent } from "./perks-history.component"
import { PromContentHistoryComponent } from "./prom-content-history.component"

export interface HistoryDialogData {
    masterId: string
}

@Component({
    selector: "valben-history-dialog",
    templateUrl: "./history-dialog.component.html",
    standalone: true,
    imports: [
        NgSwitch,
        NgSwitchCase,
        NgSwitchDefault,
        MatDialogTitle,
        MatDialogContent,
        MatDialogClose,
        MatIcon,
        SurveyHistoryComponent,
        BenefitsHistoryComponent,
        CategoriesHistoryComponent,
        LocationsHistoryComponent,
        AwardsHistoryComponent,
        NewsHistoryComponent,
        PerksHistoryComponent,
        PromContentHistoryComponent,
    ],
})
export class HistoryDialogComponent {
    readonly data = inject<HistoryDialogData>(MAT_DIALOG_DATA)

    readonly historyEntities = signal<any[]>([])
    readonly collection = signal<string>("")
    readonly error = signal<string>("")
    readonly loading = signal<boolean>(true)

    private entity: any

    private readonly _historyService = inject(HistoryService)
    private readonly _http = inject(HttpClient)
    private readonly _context = inject(ContextService)

    constructor() {
        this._context.InitDependencies()

        this._historyService
            .Get(this.data.masterId)
            .pipe(
                switchMap(res => {
                    const versions = [...res].sort((a, b) => b.currentVersion - a.currentVersion)
                    versions.pop()
                    if (versions.length === 0) return of(null)
                    const collection = res[0].collectionName
                    const route = (environment.endpointRoutes as Record<string, string | undefined>)[collection]
                    if (!route) return throwError(() => new Error(`Unknown collection: ${collection}`))

                    return this._http
                        .get<any>(`${environment.apiBaseUrl}${route}/${this.data.masterId}`)
                        .pipe(map(entity => ({ versions, collection, entity })))
                }),
                takeUntilDestroyed(),
            )
            .subscribe({
                next: result => {
                    this.loading.set(false)
                    if (!result) {
                        this.error.set("Najnovija verzija")
                        return
                    }

                    this.entity = result.entity

                    const built: any[] = []
                    for (const item of result.versions) {
                        built.push(
                            this._diff(
                                built,
                                item.modifiedDate,
                                item.currentVersion - 1,
                                item.modifiedByUser,
                                item.oldValues,
                            )
                        )
                    }
                    this.historyEntities.set(built)
                    this.collection.set(result.collection)
                },
                error: () => {
                    this.loading.set(false)
                    this.error.set("Greška prilikom dohvaćanja promjena")
                },
            })
    }

    private _diff(
        previous: any[],
        modDate: Date,
        lastVersion: number,
        modUser: string,
        oldValues: Record<string, any>
    ): object {
        const arrRef: any[][] = []
        const retObj = JSON.parse(JSON.stringify(
            previous.length === 0
                ? this.entity
                : previous[previous.length - 1]
        ))

        for (const field in oldValues) {
            const splittedFields = field.split(".")
            let retObjField = retObj

            for (let i = 0; i < splittedFields.length; i++) {
                if (i === splittedFields.length - 1) {
                    if (oldValues[field] === null) {
                        if (!arrRef.includes(retObjField) && Array.isArray(retObjField)) {
                            arrRef.push(retObjField)
                        }
                        delete retObjField[splittedFields[i]]
                    } else {
                        retObjField[splittedFields[i]] = oldValues[field]
                    }
                } else {
                    if (retObjField[splittedFields[i]]) {
                        retObjField = retObjField[splittedFields[i]]
                    } else {
                        retObjField[splittedFields[i]] = {}
                        retObjField = retObjField[splittedFields[i]]
                    }
                }
            }
        }

        for (const arr of arrRef) {
            for (let i = arr.length - 1; i >= 0; i--) {
                if (!arr[i]) arr.splice(i, 1)
            }
        }

        retObj.modifiedDate = new Date(modDate)
        retObj.version = lastVersion
        retObj.modifiedByUser = modUser

        return retObj
    }
}
